import { useEffect, useRef, useState } from "react";
import { Check, ChevronsUpDown, Loader2, Search, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CustomerAPI } from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * Debounced server-side customer picker.
 * `value` = customer id; `onChange(id, customerObject)` fires on selection.
 */
export function CustomerCombobox({ value, onChange, placeholder = "Search customers by name or phone…", testid = "customer-combobox" }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [picked, setPicked] = useState(null);
    const debounceRef = useRef(null);

    // Initial fetch when value is provided externally (e.g. ?customer_id=...)
    useEffect(() => {
        if (value && (!picked || picked.id !== value)) {
            CustomerAPI.get(value).then((c) => setPicked(c)).catch(() => {});
        } else if (!value) {
            setPicked(null);
        }
    }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

    // Debounced search
    useEffect(() => {
        if (!open) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const params = { page: 1, page_size: 20 };
                if (search.trim()) params.q = search.trim();
                const res = await CustomerAPI.list(params);
                setResults(res.items || []);
            } catch {
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 220);
        return () => debounceRef.current && clearTimeout(debounceRef.current);
    }, [search, open]);

    const label = picked
        ? `${picked.display_name || picked.first_name} · ${picked.phone}`
        : null;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className="w-full justify-between font-normal"
                    data-testid={testid}
                >
                    <span className="flex items-center gap-2 truncate">
                        <User className="h-4 w-4 shrink-0 text-zinc-400" />
                        {label ? (
                            <span className="truncate">
                                {picked.display_name || picked.first_name}
                                <span className="ml-1 font-mono text-xs text-zinc-500">· {picked.phone}</span>
                            </span>
                        ) : (
                            <span className="text-zinc-400">{placeholder}</span>
                        )}
                    </span>
                    <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command shouldFilter={false}>
                    <div className="flex items-center border-b px-3" cmdk-input-wrapper="">
                        <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                        <CommandInput
                            placeholder="Name, phone, or customer code…"
                            value={search}
                            onValueChange={setSearch}
                            data-testid={`${testid}-search`}
                            className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
                        />
                        {loading && <Loader2 className="ml-2 h-4 w-4 animate-spin text-zinc-400" />}
                    </div>
                    <CommandList>
                        <CommandEmpty>
                            <div className="px-3 py-6 text-center text-sm text-zinc-500">
                                {loading ? "Searching…" : "No customers match."}
                            </div>
                        </CommandEmpty>
                        <CommandGroup>
                            {results.map((c) => (
                                <CommandItem
                                    key={c.id}
                                    value={c.id}
                                    onSelect={() => {
                                        setPicked(c);
                                        onChange && onChange(c.id, c);
                                        setOpen(false);
                                    }}
                                    data-testid={`${testid}-option-${c.id}`}
                                    className="flex cursor-pointer items-center gap-2"
                                >
                                    <Check
                                        className={cn(
                                            "h-4 w-4",
                                            value === c.id ? "opacity-100" : "opacity-0"
                                        )}
                                    />
                                    <div className="min-w-0 flex-1">
                                        <div className="truncate text-sm font-medium">
                                            {c.display_name || c.first_name}
                                        </div>
                                        <div className="flex items-center gap-2 text-xs text-zinc-500">
                                            <span className="font-mono">{c.phone}</span>
                                            <span className="font-mono text-zinc-400">· {c.customer_code}</span>
                                        </div>
                                    </div>
                                    {c.vehicle_count > 0 && (
                                        <span className="ml-2 rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-mono text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400">
                                            {c.vehicle_count} veh
                                        </span>
                                    )}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
